/**
 * pipMoments — pure moment-table logic for the Pip governor (no React).
 *
 * Spec: .plan/features/2026-06-10-pip-mascot-placement.md §3 (governor) + Rev 3.3.
 *
 * This module owns the moment → action mapping, frequency policy, and the
 * fail-open storage ledger. It is deliberately React-free so the §3 table can
 * be unit-tested as plain functions; the React surface (usePipMoments /
 * PipMomentsProvider) is a thin shell over `recordAndShouldFire`.
 *
 * Frequency policy (plan §3 table):
 *   - `once-ever`     → persisted in localStorage `pip.firsts.v1` (JSON string set)
 *   - `once-session`  → persisted in sessionStorage `pip.session.v1`
 *   - `every-time`    → never persisted; always fires
 *
 * Storage rule (plan §3): failures FAIL OPEN. Every storage access is wrapped
 * so a throw (private mode, quota, disabled storage) never propagates — an
 * animation may repeat, but it must never throw (Rule 8 spirit). On a storage
 * read/write failure a `once-ever` / `once-session` moment is treated as
 * not-yet-consumed (so it fires; repeats are acceptable per the plan).
 *
 * Storage keys are the plan's verbatim values (`pip.firsts.v1`,
 * `pip.session.v1`). They are NOT `proof-`-prefixed: the `proof-*` rule governs
 * Cloudflare resources, not browser-storage keys (cf. `proof-theme` in
 * useTheme.ts is a separate convention). The plan §3 fixes these names.
 *
 * Reduced motion (plan §3): the first is still RECORDED (storage still written)
 * — the component's `prefers-reduced-motion` CSS kills the visual, so there is
 * no JS branch here for reduced motion. The governor records identically; only
 * the paint differs.
 */

import type { PipAction } from "./PipMascot";

// ── Moment keys + table ─────────────────────────────────────────────────────────

/**
 * The closed set of moment keys a placement may fire. Each maps to exactly one
 * Pip action and one frequency policy (plan §3 table).
 */
export type PipMomentKey =
  | "first-recipe"
  | "first-cook"
  | "first-share"
  | "peak-five-star"
  | "list-end"
  | "save-failed"
  | "goto-promoted"
  | "working";

type PipMomentFrequency = "once-ever" | "once-session" | "every-time";

interface PipMomentDef {
  /** The Pip action this moment plays. */
  action: PipAction;
  /** How often the moment is allowed to fire. */
  frequency: PipMomentFrequency;
}

/**
 * The §3 moment table. The single source of truth for action + frequency.
 * `working` → knead is a `loopWhile` action bounded by host state, not a
 * fire-once event; it is included here for completeness but the provider drives
 * it through a dedicated setWorking() handle rather than the one-shot fire path
 * (see usePipMoments.ts).
 */
export const PIP_MOMENTS: Readonly<Record<PipMomentKey, PipMomentDef>> = {
  "first-recipe": { action: "congratulate", frequency: "once-ever" },
  "first-cook": { action: "congratulate", frequency: "once-ever" },
  "first-share": { action: "congratulate", frequency: "once-ever" },
  "peak-five-star": { action: "tumble", frequency: "once-ever" },
  "list-end": { action: "greet", frequency: "once-session" },
  "save-failed": { action: "settle", frequency: "every-time" },
  "goto-promoted": { action: "promote", frequency: "every-time" },
  working: { action: "knead", frequency: "every-time" },
} as const;

// ── Storage keys ────────────────────────────────────────────────────────────────

/** localStorage key for once-ever firsts (JSON array of consumed keys). */
export const FIRSTS_STORAGE_KEY = "pip.firsts.v1" as const;
/** sessionStorage key for once-per-session moments (JSON array). */
export const SESSION_STORAGE_KEY = "pip.session.v1" as const;

// ── Fail-open storage helpers ───────────────────────────────────────────────────

/**
 * Read a JSON string-set from the given Storage. Returns an empty set on any
 * failure (missing storage, throw, malformed JSON) — fail-open: an unreadable
 * ledger is treated as "nothing consumed yet" so the moment fires.
 */
function readConsumedSet(storage: Storage | undefined, key: string): Set<string> {
  if (!storage) return new Set();
  try {
    const raw = storage.getItem(key);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((v): v is string => typeof v === "string"));
    }
    return new Set();
  } catch {
    // Storage disabled (private mode), quota, or malformed JSON — fail open.
    return new Set();
  }
}

/**
 * Persist a JSON string-set to the given Storage. Swallows any throw — a write
 * failure means the moment may replay later, which the plan explicitly accepts;
 * it must never propagate (Rule 8 spirit).
 */
function writeConsumedSet(storage: Storage | undefined, key: string, set: Set<string>): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify([...set]));
    // eslint-disable-next-line no-restricted-syntax -- best-effort: Pip animation ledger; on quota/disabled storage the moment may replay, explicitly accepted by the plan (Rule 8 documented degradation).
  } catch {
    // Quota / disabled storage — fail open (animation may repeat). Never throw.
  }
}

/** Resolve a Storage object, guarding SSR + access throws. Returns undefined when unavailable. */
function safeStorage(kind: "local" | "session"): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    // Accessing the property can itself throw in some locked-down browsers.
    return undefined;
  }
}

// ── Frequency decision + ledger write ───────────────────────────────────────────

/**
 * The outcome of evaluating a moment key against the frequency ledger.
 *   - `fire`: whether the action should play now.
 *   - `action`: the Pip action for this key.
 *
 * Calling this for a `once-ever` / `once-session` key has the SIDE EFFECT of
 * marking the key consumed (writing storage) when it fires — so a second call
 * with the same key returns `fire: false`. This is intentional: the governor's
 * "consumed" semantics are write-on-decide, which is what makes a preempted
 * `congratulate` (still routed through this function) stay consumed even when
 * its animation is suppressed by a colliding `settle` (plan §3).
 *
 * `every-time` keys always return `fire: true` and never touch storage.
 */
export function recordAndShouldFire(key: PipMomentKey): { fire: boolean; action: PipAction } {
  const def = PIP_MOMENTS[key];

  if (def.frequency === "every-time") {
    return { fire: true, action: def.action };
  }

  const isLocal = def.frequency === "once-ever";
  const storageKey = isLocal ? FIRSTS_STORAGE_KEY : SESSION_STORAGE_KEY;
  const storage = safeStorage(isLocal ? "local" : "session");

  const consumed = readConsumedSet(storage, storageKey);
  if (consumed.has(key)) {
    return { fire: false, action: def.action };
  }

  // Mark consumed now (write-on-decide). On a storage failure the write is a
  // no-op and the moment may replay on a later session/device — accepted by the
  // plan (cross-device firsts repeat is bounded + self-healing).
  consumed.add(key);
  writeConsumedSet(storage, storageKey, consumed);

  return { fire: true, action: def.action };
}

/**
 * Resolve the action for a key without touching the ledger. Used by the
 * provider when it needs the action of an already-decided moment (e.g. the
 * preempt-precedence comparison).
 */
export function actionForMoment(key: PipMomentKey): PipAction {
  return PIP_MOMENTS[key].action;
}

/**
 * Test-only: clear both ledgers. Exported for unit tests so each case starts
 * from a clean storage state. Not used by application code.
 * @internal
 */
export function __clearPipMomentLedgers(): void {
  const local = safeStorage("local");
  const session = safeStorage("session");
  try {
    local?.removeItem(FIRSTS_STORAGE_KEY);
    // eslint-disable-next-line no-restricted-syntax -- best-effort: test-only ledger clear; disabled/unavailable storage is a no-op (Rule 8 documented degradation).
  } catch {
    /* fail open */
  }
  try {
    session?.removeItem(SESSION_STORAGE_KEY);
    // eslint-disable-next-line no-restricted-syntax -- best-effort: test-only ledger clear; disabled/unavailable storage is a no-op (Rule 8 documented degradation).
  } catch {
    /* fail open */
  }
}
